import { PermissionGate } from "@components/PermissionGate";
import { useTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  Group,
  Modal,
  Select,
  Stack,
  TextInput,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";

import { useCreatePartner } from "@hooks/mutations/partner/useCreatePartner";
import { PracticeType } from "@customTypes/partner";

interface AddPartnerModalProps {
  opened: boolean;
  onClose: () => void;
}

export const AddPartnerModal = ({ opened, onClose }: AddPartnerModalProps) => {
  const { t } = useTranslation();
  const { mutate: createPartner, isLoading } = useCreatePartner();

  const form = useForm({
    initialValues: {
      name: "",
      enterprise_id: "",
      practice_type: "clinic" as PracticeType,
      is_organization: true,
      contact_email: "",
      contact_phone: "",
      address: "",
      city: "",
      registration_number: "",
      tax_id: "",
    },
    validate: {
      name: (value) => (value.trim().length < 2 ? t("invalid-name") : null),
      practice_type: (value) => (value ? null : t("required-field")),
    },
  });

  const handleSubmit = (values: typeof form.values) => {
    createPartner(values, {
      onSuccess: () => {
        form.reset();
        onClose();
      },
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600} fz={18}>{t("add-partner")}</Text>}
      size="lg"
      centered
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextInput
            label={t("name")}
            placeholder={t("enter-name")}
            withAsterisk
            {...form.getInputProps("name")}
          />

          <Group grow>
            <TextInput
              label={t("enterprise-id")}
              placeholder={t("enter-id")}
              {...form.getInputProps("enterprise_id")}
            />
            <Select
              label={t("practice-type")}
              placeholder={t("select")}
              withAsterisk
              data={[
                { label: t("individual-practice"), value: "individual_practice" },
                { label: t("group-practice"), value: "group_practice" },
                { label: t("hospital"), value: "hospital" },
                { label: t("clinic"), value: "clinic" },
                { label: t("laboratory"), value: "laboratory" },
                { label: t("pharmacy"), value: "pharmacy" },
                { label: t("other"), value: "other" },
              ]}
              {...form.getInputProps("practice_type")}
            />
          </Group>

          <Checkbox
            label={t("is-organization")}
            description={t("is-organization-description")}
            {...form.getInputProps("is_organization", { type: "checkbox" })}
          />

          <Group grow>
            <TextInput
              label={t("contact-email")}
              placeholder="email@example.com"
              {...form.getInputProps("contact_email")}
            />
            <TextInput
              label={t("contact-phone")}
              placeholder="+233..."
              {...form.getInputProps("contact_phone")}
            />
          </Group>

          <TextInput
            label={t("address")}
            placeholder={t("enter-address")}
            {...form.getInputProps("address")}
          />

          <Group grow>
            <TextInput
              label={t("registration-number")}
              placeholder="REG-123..."
              {...form.getInputProps("registration_number")}
            />
            <TextInput
              label={t("tax-id")}
              placeholder="TAX-456..."
              {...form.getInputProps("tax_id")}
            />
          </Group>

          <Group justify="flex-end" mt="xl">
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              {t("cancel")}
            </Button>
            <PermissionGate permission="partners.create"><Button type="submit" loading={isLoading}>
              {t("save")}
            </Button></PermissionGate>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};
